import { inject, Injectable } from "@angular/core";
import { catchError, first, map, mergeMap, switchMap, timeout } from "rxjs/operators";
import { firstValueFrom, from, Observable, of, race, TimeoutError } from "rxjs";
import { AppState } from "features";
import { Action, Store } from "@ngrx/store";
import {
	selectData,
	selectInited,
	selectResultInsi,
	selectResultsAfterInit,
	selectStoppedWithError,
} from "../store/lecteur/lecteur.selector";
import { LecteurDialogService } from "@featuresAY/lecteur/services/lecteur-dialog.service";
import { AppelInsi } from "../store/lecteur/lecteur.action";
import { UiKitDialogService } from "@jack-sparrow-libraries/ui-kit";
import {
	EnsiCREnum,
	PatientInsiInfo,
	ReponseDataInsi,
	ReponseInsi,
	ReponseInsiAction,
	ReponseInsiParsed,
	TypeErreurInsi,
} from "@models/insi.models";
import { AddPlaceOfBirthModalInsiWebAyComponent } from "@featuresAY/patient-web-ay/modals/add-place-of-birth-modal-insi-web-ay/add-place-of-birth-modal-insi-web-ay.component";
import { LoadingAyWebService } from "@services/loading-ay-web.service";
import * as Sentry from "@sentry/angular";

@Injectable({
	providedIn: "root",
})
export class LecteurManagerService {
	private readonly store = inject(Store<AppState>);
	private readonly lecteurDialogService = inject(LecteurDialogService);
	private readonly loadingService = inject(LoadingAyWebService);
	private readonly dialogService = inject(UiKitDialogService);

	manageOptionHideLoaderObservableObject(options: any, toReturn: any): Observable<any> {
		if (options?.loader) {
			return from(this.lecteurDialogService.hideReadLecteurLoading()).pipe(
				map(() => {
					return toReturn;
				}),
			);
		}
		return of(toReturn);
	}

	manageOptionShowLoaderObservable(options: any, action: Action | Action[]): Action[] | Observable<Action> {
		if (options?.loader) {
			return from(this.lecteurDialogService.showLoading(options?.loader?.title)).pipe(
				mergeMap(() => [].concat(action)),
			);
		}
		return [].concat(action);
	}

	manageOptionHideLoader(options: any, handler: any): void {
		if (options?.loader && this.loadingService.isActive) {
			this.loadingService.hide().then(() => {
				handler();
			});
		} else {
			handler();
		}
	}

	waitForErrorProcessLecteur(): Observable<boolean> {
		return this.store.select(selectStoppedWithError).pipe(
			first(Boolean),
			map(() => false),
		);
	}

	waitForSuccessProcessLecteur(): Observable<boolean> {
		return this.store.select(selectResultsAfterInit).pipe(
			first(Boolean),
			map(() => true),
		);
	}

	async traiteReponseInsi(reponseInsi: ReponseInsi): Promise<ReponseInsiParsed> {
		// TODO Here we should parse the insi response and  treat the several cases of insi response (showing alert dialogs in error cases)
		if (reponseInsi.cr === EnsiCREnum.UNE_IDENTITE_TROUVEE) {
			return {
				returnAction: ReponseInsiAction.SUCCESS,
				typeErreur: TypeErreurInsi.Aucune,
				diagnostic: reponseInsi.diagnostic,
			};
		} else if (reponseInsi.cr === EnsiCREnum.PLUSIEURS_IDENTITES_TROUVEES) {
			return {
				returnAction: ReponseInsiAction.PARAMS_MISSING,
				typeErreur: TypeErreurInsi.TechniqueNonBloquant,
				diagnostic: reponseInsi.diagnostic,
			};
		}
		return {
			returnAction: ReponseInsiAction.ERROR,
			typeErreur: TypeErreurInsi.Fonctionnelle,
			diagnostic: reponseInsi.diagnostic,
		};
	}

	async resolveInsiPlusieursIdentitesTrouves() {
		const result = await firstValueFrom(
			this.dialogService
				.openDialog(AddPlaceOfBirthModalInsiWebAyComponent, {
					title: "Plusieurs identités trouvées",
					subtitle: "",
					closeButton: true,
					noPadding: false,
					radius: "lg",
				})
				.afterClosed(),
		);
		return result;
	}

	async dispatchInsiCall(patientInsiinfo: PatientInsiInfo) {
		return await this.manageInsiProcess(patientInsiinfo);
	}

	async manageInsiProcess(patientInsiinfo: PatientInsiInfo): Promise<any> {
		await this.loadingService.showWaitLoadingWorkeuse("Récupération des infos patient...", "INSI");

		const reset$ = this.store.select(selectInited).pipe(first(Boolean));

		const promise = firstValueFrom(
			reset$.pipe(
				switchMap(() =>
					race([this.waitForErrorProcessLecteur(), this.waitForSuccessProcessLecteur()]).pipe(
						// pour éviter un blocage comme sur le ticket AY-9808, je mets un timeout de 30 sec
						timeout(30000),
						catchError((error) => {
							if (error instanceof TimeoutError) {
								Sentry.captureException(error);
							}
							return of(false);
						}),
					),
				),
			),
		);

		this.store.dispatch(new AppelInsi(patientInsiinfo));
		const processed = await promise;

		if (this.loadingService.isActive) {
			await this.loadingService.hide();
		}

		const result: ReponseDataInsi = await firstValueFrom(this.store.select(selectData));
		if (processed && result.typeErreur === TypeErreurInsi.Aucune) {
			return await firstValueFrom(this.store.select(selectResultInsi));
		}

		return null;
	}
}
